<!-- CategoriesView.svelte -->

<script>
  import { createEventDispatcher } from 'svelte';
  import { onMount, onDestroy } from 'svelte';
  import { Capacitor } from '@capacitor/core';
  import { App } from '@capacitor/app';

  let showUpdatePrompt = false;
  let isUpdating = false;
  const FRONTEND_VERSION = "3.1";

  let categories = [];
  let isLoading = true;
  let showMenu = false;
  let isScrolling = false;
  let activeTab = 'home';
  let showFruits = false;

  let regularCategories = [];
  let specialCategories = [];

  let activePickups = [];
  let numberOfDeliveries = 0;
  let showPickupsPopup = false;
  let showFloatingIcon = false;

  // New state for user profile and delivery point
  let userProfile = null;
  let isLoadingProfile = true;

  const CACHE_KEY = 'categoriesCache';
  const CACHE_DURATION = 2000; //in milliseconds

  const gradients = [
    'linear-gradient(90deg, #004AAD, #9309BB)',
    'linear-gradient(90deg, #F41212, #B42B40)',
    'linear-gradient(90deg, #026402, #9E9F04)',
    'linear-gradient(90deg, #FF512F, #DD2476)',
    'linear-gradient(90deg, #11998E, #38EF7D)',
    'linear-gradient(90deg, #FFA17F, #00223E)',
    'linear-gradient(90deg, #FF7E5F, #FEB47B)',
    'linear-gradient(90deg, #9D50BB, #6E48AA)',
    'linear-gradient(90deg, #FF8A00, #FF5722)',
    'linear-gradient(90deg, #FF416C, #FF4B2B)'
  ];

  const fruits = [
    { shape: '🍎', color: 'linear-gradient(45deg, #ff0000, #ff6b6b)' },
    { shape: '🍊', color: 'linear-gradient(45deg, #ffa500, #ffd700)' },
    { shape: '🍇', color: 'linear-gradient(45deg, #8e44ad, #9b59b6)' },
    { shape: '🍓', color: 'linear-gradient(45deg, #e74c3c, #ff6b6b)' },
    { shape: '🍐', color: 'linear-gradient(45deg, #2ecc71, #7bed9f)' },
    { shape: '🍋', color: 'linear-gradient(45deg, #f1c40f, #ffeaa7)' },
    { shape: '🍑', color: 'linear-gradient(45deg, #ffb8a8, #ffdab9)' },
    { shape: '🍍', color: 'linear-gradient(45deg, #ffd700, #32cd32)' },
    { shape: '🍉', color: 'linear-gradient(45deg, #ff6b6b, #32cd32)' },
    { shape: '🥝', color: 'linear-gradient(45deg, #9acd32, #32cd32)' },
    { shape: '🍒', color: 'linear-gradient(45deg, #dc143c, #ff1493)' },
    { shape: '🍌', color: 'linear-gradient(45deg, #ffd700, #fffacd)' },
    { shape: '🥭', color: 'linear-gradient(45deg, #ff8c00, #ffa500)' },
    { shape: '🥑', color: 'linear-gradient(45deg, #006400, #32cd32)' },
    { shape: '🍈', color: 'linear-gradient(45deg, #f0fff0, #98fb98)' },
    { shape: '🍅', color: 'linear-gradient(45deg, #ff6347, #ff4500)' },
    { shape: '🍆', color: 'linear-gradient(45deg, #4b0082, #8a2be2)' },
    { shape: '🥥', color: 'linear-gradient(45deg, #8b4513, #d2691e)' },
    { shape: '🥦', color: 'linear-gradient(45deg, #228b22, #32cd32)' },
    { shape: '🥕', color: 'linear-gradient(45deg, #ff7f50, #ffa500)' },
    { shape: '🌽', color: 'linear-gradient(45deg, #fff700, #fffacd)' },
    { shape: '🍏', color: 'linear-gradient(45deg, #98fb98, #00fa9a)' },
  ];



	function formatDate(dateString) {
	  const date = new Date(dateString);
	  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
	}

  function openAboutUs() {
    dispatch('openAboutUs');
    toggleMenu();
  }

  function openTerms() {
    dispatch('openTerms');
    toggleMenu();
  }

  function openPrivacy() {
    dispatch('openPrivacy');
    toggleMenu();
  }

  async function checkForUpdates() {
    try {
      const response = await fetch('https://groupfruits.online/shop/api/CurrentVersionView/');
      const data = await response.json();
      const currentVersion = data.current_version;

      const versionDifference = parseFloat(currentVersion) - parseFloat(FRONTEND_VERSION);

      if (versionDifference >= 0.1) {
        const lastPromptDateTime = localStorage.getItem('last_update_prompt_date_time');
        const currentTime = new Date().getTime();

        if (!lastPromptDateTime || (currentTime - parseInt(lastPromptDateTime)) >= 48 * 60 * 60 * 1000) {
          showUpdatePrompt = true;
          localStorage.setItem('last_update_prompt_date_time', currentTime.toString());
        }
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  }

  function closeUpdatePrompt() {
    showUpdatePrompt = false;
  }

  async function handleUpdate() {
    isUpdating = true;
    await new Promise(resolve => setTimeout(resolve, 1000)); // Simulating update process
    window.open('https://play.google.com/store/apps/details?id=store.groupmarket.app', '_blank');
    isUpdating = false;
  }

  async function fetchActivePickups() {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch('https://groupfruits.online/shop/api/PickupCodesView/', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `JWT ${accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const data = await response.json();
      activePickups = data.deliveries;
      numberOfDeliveries = data.number_of_deliveries;
      showFloatingIcon = numberOfDeliveries > 0;
    } catch (err) {
      console.error('Error fetching active pickups:', err);
    }
  }

  // New function to fetch user profile
  async function fetchUserProfile() {
    isLoadingProfile = true;
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch('https://groupfruits.online/users/api/GetUserProfileView/', {
        headers: {
          'Authorization': `JWT ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      userProfile = await response.json();
    } catch (err) {
      console.error('Error fetching user profile:', err);
    } finally {
      isLoadingProfile = false;
    }
  }

  function toggleMenu() {
    showMenu = !showMenu;
    if (!showMenu) {
      activeTab = 'home';
    }
  }

  function getCachedCategories() {
    const cachedData = localStorage.getItem(CACHE_KEY);
    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      }
    }
    return null;
  }

  function setCachedCategories(data) {
    const cacheData = {
      data: data,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  }

  async function fetchCategories(forceRefresh = false) {
    if (!forceRefresh) {
      const cachedData = getCachedCategories();
      if (cachedData) {
        regularCategories = cachedData.regularCategories;
        specialCategories = cachedData.specialCategories;
        isLoading = false;
        return;
      }
    }

    isLoading = true;
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch('https://groupfruits.online/shop/api/UserCategoriesOrganizedView/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `JWT ${accessToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        regularCategories = data.regular_categories;
        specialCategories = data.special_categories;
        setCachedCategories({ regularCategories, specialCategories });
      } else {
        const errorResponse = await response.json();
        if (errorResponse.detail === 'Authentication credentials were not provided.' ||
          errorResponse.detail === 'Given token not valid for any token type') {
          dispatch('loginNeeded');
        } else if (errorResponse.message === 'Delivery point not set') {
          dispatch('pointNeeded');
        } else if (errorResponse.message === 'Full name not set') {
          dispatch('loginSuccessNoName');
        } else {
          console.error('Error fetching categories:', response.statusText);
        }
      }
    } catch (error) {
      console.error('Network error:', error);
    } finally {
      isLoading = false;
    }
  }

  const dispatch = createEventDispatcher();

  function handleCategoryClick(categoryId) {
    console.log('Category clicked:', categoryId);
    dispatch('categorySelected', { categoryId });
  }

  function togglePickupsPopup() {
    showPickupsPopup = !showPickupsPopup;
  }

  function closePickupsPopup() {
    showPickupsPopup = false;
  }

  function handleVisibilityChange() {
    if (!document.hidden) {
      fetchCategories(true);
    }
  }

  function changePickupPoint() {
    dispatch('pleaseChangeDeliveryPoint');
  }

  function flushOldCarts() {
    const oneHourAgo = Date.now() - (60* 60 * 1000);

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith('cart-')) {
        const cartData = JSON.parse(localStorage.getItem(key));
        if (new Date(cartData.lastUpdated).getTime() < oneHourAgo) {
          localStorage.removeItem(key);
          console.log(`Flushed old cart: ${key}`);
        }
      }
    }
  }


onMount(() => {
  fetchCategories();
  fetchActivePickups();
  fetchUserProfile(); // Fetch the user profile
  flushOldCarts();

  setTimeout(() => {
    showFruits = true;
  }, 3000);

  setTimeout(() => {
    checkForUpdates();
  }, 5000);

  const animatedTopArea = document.getElementById('animatedTopArea');
  const fruitBackground = document.querySelector('.fruit-background');
  let lastScrollTop = 0;
  let scrollTimeout;
  let reappearTimeout;

  window.addEventListener('scroll', () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (animatedTopArea) {  // Add this check
      if (scrollTop > lastScrollTop) {
        // Scrolling down
        animatedTopArea.classList.add('collapsed');
      } else {
        // Scrolling up
        animatedTopArea.classList.remove('collapsed');
      }
    }
    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

    // Hide fruits when scrolling starts
    if (!isScrolling && fruitBackground) {  // Add this check
      isScrolling = true;
      fruitBackground.style.opacity = '0';
    }

    // Clear both timeouts if they're set
    clearTimeout(scrollTimeout);
    clearTimeout(reappearTimeout);

    // Set a timeout to run after scrolling ends
    scrollTimeout = setTimeout(() => {
      isScrolling = false;

      // Set another timeout for the fruits to reappear
      reappearTimeout = setTimeout(() => {
        if (fruitBackground) {  // Add this check
          fruitBackground.style.opacity = '1';
        }
      }, 1500); // 1.5 second delay before reappearing
    }, 100); // Adjust this value to change how quickly the scroll is considered to have stopped
  }, false);

    window.addEventListener('popstate', () => {
      if (showPickupsPopup) {
        closePickupsPopup();
      }
    });

    document.addEventListener("visibilitychange", handleVisibilityChange);


	  if (Capacitor.isNativePlatform()) {
		App.addListener('backButton', () => {
		  if (showPickupsPopup) {
			closePickupsPopup();
		  } else {
			App.exitApp();
		  }
		});
	  }


  });


onDestroy(() => {
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  if (Capacitor.isNativePlatform()) {
    App.removeAllListeners();
  }
});

</script>








<style>
  * {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  :global(body) {
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }

  .bottom-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    background-color: #ffffff;
    display: flex;
    justify-content: space-around;
    align-items: center;
    height: 3.625rem;
    box-shadow: 0 -0.125rem 0.625rem rgba(0, 0, 0, 0.1);
    z-index: 1000;
    padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
    padding-top: 0.25rem;
  }

  .update-prompt-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: flex-end;
    z-index: 2000;
  }

  .update-prompt {
    background-color: white;
    border-radius: 1.25rem 1.25rem 0 0;
    padding: 1.25rem;
    width: 100%;
    max-width: 25rem;
    box-shadow: 0 -0.25rem 0.625rem rgba(0, 0, 0, 0.1);
    animation: slideUp 0.3s ease-out;
  }

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }

  .update-prompt h2 {
    font-size: 1.5rem;
    margin-bottom: 0.625rem;
  }

  .update-prompt p {
    font-size: 1rem;
    margin-bottom: 1.25rem;
  }

  .update-prompt-buttons {
    display: flex;
    justify-content: space-between;
  }

  .update-button, .close-button {
    padding: 0.625rem 1.25rem;
    border-radius: 0.3125rem;
    font-size: 1rem;
    cursor: pointer;
    transition: background-color 0.3s;
  }

  .update-button {
    background-color: #4CAF50;
    color: white;
    border: none;
  }

  .close-button {
    background-color: #f1f1f1;
    color: #333;
    border: none;
  }

  .update-button:hover { background-color: #45a049; }
  .close-button:hover { background-color: #e0e0e0; }

  .update-dot-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(76, 175, 80, 0.8);
    border-radius: 0.3125rem;
  }

  .update-dot {
    width: 0.5rem;
    height: 0.5rem;
    background-color: white;
    border-radius: 50%;
    margin: 0 0.25rem;
    animation: updateBounce 0.5s ease-in-out infinite;
  }

  .update-dot:nth-child(2) { animation-delay: 0.1s; }
  .update-dot:nth-child(3) { animation-delay: 0.2s; }

  @keyframes updateBounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-0.625rem); }
  }

  .category-container {
    padding: 1.875rem 2.1875rem;
    height: 9.375rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 0.625rem;
    box-shadow: 0 0.25rem 0.375rem rgba(0, 0, 0, 0.1);
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    overflow: hidden;
    margin: 0.3125rem;
  }

  .category-container::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .category-container:hover {
    transform: translateY(-0.3125rem);
    box-shadow: 0 0.375rem 0.75rem rgba(0, 0, 0, 0.15);
  }

  .category-container:hover::before { opacity: 1; }

  .category-container:active {
    transform: translateY(-0.125rem);
    box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.2);
  }

  .category-container h3 {
    color: white;
    font-size: 1.7rem;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    position: relative;
    z-index: 1;
    text-shadow: 0.0625rem 0.0625rem 0.1875rem rgba(0, 0, 0, 0.2);
    transition: transform 0.3s ease;
  }

  .category-container:hover h3 { transform: scale(1.05); }

  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4); }
    70% { box-shadow: 0 0 0 0.625rem rgba(255, 255, 255, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
  }

  .category-container:focus {
    outline: none;
    animation: pulse 1.5s infinite;
  }

  .loader-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #ffffff;
    z-index: 2000;
  }

  .dot-loader {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .dot {
    width: 1.25rem;
    height: 1.25rem;
    background-color: #DD2E44;
    border-radius: 50%;
    margin: 0 0.625rem;
    animation: dotbounce 0.30s ease-in-out infinite;
  }

  .dot:nth-child(2) { animation-delay: 0.1s; }
  .dot:nth-child(3) { animation-delay: 0.2s; }

  @keyframes dotbounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-0.625rem); }
  }

  .nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #777;
    font-size: 0.8rem;
    transition: color 0.3s ease;
  }

  .nav-item.active { color: #DD2E44; }

  .nav-icon {
    font-size: 1.5rem;
    margin-bottom: 0.125rem;
  }

  .nav-item.active .nav-icon { color: #DD2E44; }

  .nav-item.pickup-button {
    position: relative;
    width: 3.125rem;
    height: 3.125rem;
    background: linear-gradient(90deg, #FF416C, #FF4B2B);
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    box-shadow: 0 0.125rem 0.3125rem rgba(255, 107, 107, 0.3);
    transition: all 0.3s ease;
    animation: pickupPulse 2s infinite;
  }

  .nav-item.pickup-button:active { transform: scale(0.95); }

  .pickup-text {
    font-size: 0.625rem;
    font-weight: bold;
    color: white;
  }

  .pickup-badge {
    position: absolute;
    top: -0.3125rem;
    right: -0.3125rem;
    background: linear-gradient(90deg, #FF416C, #FF4B2B);
    color: white;
    border-radius: 50%;
    width: 1.25rem;
    height: 1.25rem;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 0.625rem;
    font-weight: bold;
    box-shadow: 0 0.125rem 0.3125rem rgba(0, 0, 0, 0.2);
  }

  @keyframes pickupPulse {
    0% { box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.4); }
    70% { box-shadow: 0 0 0 0.625rem rgba(255, 107, 107, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 107, 107, 0); }
  }

  .popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1001;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease;
  }

  .popup-overlay.show {
    opacity: 1;
    pointer-events: all;
  }

  .pickups-popup {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: white;
    padding: 1.25rem;
    border-radius: 0.625rem;
    box-shadow: 0 0.25rem 1.25rem rgba(0, 0, 0, 0.2);
    z-index: 1002;
    max-width: 90%;
    width: 37.5rem;
    max-height: 97vh;
    overflow-y: auto;
  }

  .close-popup {
    position: absolute;
    top: 0.625rem;
    right: 0.625rem;
    background: none;
    border: none;
    font-size: 2.4375rem;
    cursor: pointer;
  }

  .pickup-card {
    background-color: #f0f0f0;
    border-radius: 0.75rem;
    padding: 1.25rem;
    margin-bottom: 1.25rem;
    box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.1);
  }

  .pickup-card h3 {
    font-size: 1rem;
    margin: 0 0 0.625rem 0;
    color: #333;
  }

  .pickup-code {
    color: white;
    font-size: 2rem;
    font-weight: bold;
    display: block;
    text-align: center;
    margin-top: 0.3125rem;
  }

  .pickup-code span {
    font-size: 2.25rem;
    font-weight: bold;
    display: block;
    margin-bottom: 0.3125rem;
  }

  .pickup-code p {
    margin: 0;
    font-size: 0.875rem;
  }

  .pickup-point, .order-amount {
    font-size: 0.875rem;
    margin: 0.3125rem 0;
  }


  .pickup-code-container {
    background: linear-gradient(90deg, #026402, #9e9f04);
    border-radius: 0.75rem;
    padding: 0.9375rem 1.25rem;
    position: relative;
    margin-bottom: 0.9375rem;
    overflow: visible;
  }

  .pickup-code-label {
    background-color: black;
    color: white;
    font-size: 0.75rem;
    padding: 0.1875rem 0.5rem;
    border-radius: 0.25rem;
    position: absolute;
    top: -0.625rem;
    left: 0.9375rem;
  }

  .pickup-status {
    font-size: 1rem;
    font-weight: bold;
    color: #3B4349;
    margin-bottom: 0.3125rem;
  }

  .pickup-message {
    font-size: 1rem;
    font-weight: bold;
    color: #3B4349;
    margin-bottom: 0.625rem;
  }

  .pickup-category {
    font-size: 0.875rem;
    color: #666;
    margin-bottom: 0.9375rem;
  }

  .pickup-point {
    display: flex;
    align-items: flex-start;
    font-size: 0.875rem;
    color: #333;
    margin-top: 0.625rem;
  }

  .pickup-point-icon {
    color: #FF5722;
    margin-right: 0.3125rem;
    font-size: 1.125rem;
  }

  .pickup-point-address { flex: 1; }

  .live-label {
    position: absolute;
    top: 0.625rem;
    left: 0.625rem;
    padding: 0.3125rem 0.625rem;
    border-radius: 0.9375rem;
    font-size: 0.75rem;
    font-weight: bold;
    display: flex;
    align-items: center;
    background-color: #ff4136;
    color: white;
    animation: pulse 1.5s infinite;
  }

  .live-dot {
    width: 0.5rem;
    height: 0.5rem;
    background-color: white;
    border-radius: 50%;
    margin-left: 0.3125rem;
    animation: blink 1s infinite;
  }

  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(255, 65, 54, 0.4); }
    70% { box-shadow: 0 0 0 0.625rem rgba(255, 65, 54, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 65, 54, 0); }
  }

  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }

  .no-categories-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 60vh;
    text-align: center;
    padding: 1.25rem;
    background-color: #f8f9fa;
    border-radius: 0.625rem;
    box-shadow: 0 0.25rem 0.375rem rgba(0, 0, 0, 0.1);
    margin: 1.25rem;
  }

  .no-categories-icon {
    font-size: 4rem;
    margin-bottom: 1.25rem;
  }

  .no-categories-title {
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 0.625rem;
  }

  .no-categories-message {
    font-size: 1rem;
    color: #666;
    max-width: 18.75rem;
    margin-bottom: 1.25rem;
  }

  .refresh-button {
    background-color: #007bff;
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 0.3125rem;
    font-size: 1rem;
    cursor: pointer;
    transition: background-color 0.3s ease;
    display: flex;
    align-items: center;
  }

  .refresh-button:hover {
    background-color: #0056b3;
  }

  .refresh-icon {
    margin-right: 0.5rem;
  }

  .component-content {
    padding-top: env(safe-area-inset-top);
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
    padding-bottom: calc(3.75rem + env(safe-area-inset-bottom));
    position: relative;
    z-index: 1;
  }

  .component-content ul {
    margin: 0;
    padding: 0;
  }

  .animated-top-area {
    height: 1.8625rem;
    overflow: hidden;
    position: relative;
    transition: height 0.3s ease-out;
    z-index: 2;
    padding-top: env(safe-area-inset-top);
  }

  .animated-top-area.collapsed {
    height: 0;
  }

  .fruit-background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    opacity: 0;
    transition: opacity 0.5s ease;
  }

  .fruit-background.show {
    opacity: 1;
  }

  .fruit {
    position: absolute;
    font-size: 1rem;
    animation: fall 75s infinite linear;
    opacity: 0.8;
  }

  .fruit.top-area {
    font-size: 0.875rem;
    animation: topFall 15s infinite linear;
  }

  @keyframes topFall {
    0% { transform: translateY(-100%) rotate(0deg); }
    100% { transform: translateY(3.25rem) rotate(360deg); }
  }

  @keyframes fall {
    0% { transform: translateY(-100%) rotate(0deg); }
    100% { transform: translateY(100vh) rotate(360deg); }
  }

  .bottom-indicator {
    background: linear-gradient(90deg, #f5eeef, #ddd2d0);
    color: #535445;
    padding: 0.3125rem;
    text-align: center;
    font-size: 0.8rem;
    font-weight: bold;
    border-radius: 0.375rem;
    margin-top: 2.5rem;
    margin-bottom: 1.875rem;
    position: relative;
    overflow: hidden;
  }

  .bottom-indicator::before,
  .bottom-indicator::after {
    content: '';
    position: absolute;
    width: 1.25rem;
    height: 0.125rem;
    background: rgba(255, 255, 255, 0.7);
    animation: slide 3s infinite ease-in-out;
  }

  .bottom-indicator::before {
    top: 40%;
    left: -1.25rem;
    animation-delay: 0.5s;
  }

  .bottom-indicator::after {
    top: 60%;
    right: -1.25rem;
    animation-delay: 1.5s;
  }

  @keyframes slide {
    0% { transform: translateX(0); }
    50% { transform: translateX(3.125rem); }
    100% { transform: translateX(0); }
  }

  ul {
    list-style: none;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(15.625rem, 1fr));
    gap: 1.6875rem;
  }

  .special-trips-header {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1.875rem;
    margin-bottom: 1.25rem;
    padding: 0 2.1875rem;
  }

  .special-trips-header h2 {
    font-size: 1.5rem;
    margin-right: 0.625rem;
  }

  .triangle-icon {
    width: 0;
    height: 0;
    border-left: 0.5rem solid transparent;
    border-right: 0.5rem solid transparent;
    border-top: 0.75rem solid #333;
  }

  .category-container.out-of-stock {
    opacity: 0.5;
    pointer-events: none;
  }

  .out-of-stock-label {
    position: absolute;
    top: 0.625rem;
    right: 0.625rem;
    background-color: #ddd;
    color: #333;
    padding: 0.1875rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
  }


/* Add to the existing style section */
.support-section {
  margin: 1.5rem 0.5rem 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.support-card {
  background: white;
  border-radius: 1rem;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.support-card:active {
  transform: scale(0.98);
}

.support-icon {
  font-size: 2.5rem;
  margin-right: 1.25rem;
  background: #f5f5f5;
  width: 4rem;
  height: 4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 1rem;
}

.support-content {
  flex: 1;
}

.support-content h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #333;
}

.support-content p {
  margin: 0.25rem 0 0.75rem;
  color: #666;
  font-size: 0.9rem;
}

.support-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-weight: bold;
  text-decoration: none;
  color: white;
  transition: transform 0.2s ease;
  margin-top: 0.5rem;
}

.support-button:active {
  transform: scale(0.95);
}

.support-button-icon {
  margin-right: 0.5rem;
}

.phone-button {
  background: linear-gradient(90deg, #026402, #9E9F04);
}

.whatsapp-button {
  background: linear-gradient(90deg, #25D366, #128C7E);
}

/* New styles for pickup point header */
.pickup-point-header {
  background: white;
  margin: 0.5rem 0.5rem 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.pickup-point-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 0.25rem;
  height: 100%;
  background: linear-gradient(to bottom, #026402, #9E9F04);
}

.pickup-location-icon {
  font-size: 1.5rem;
  margin-right: 0.75rem;
  color: #DD2E44;
}

.pickup-point-info {
  flex: 1;
  overflow: hidden;
}

.pickup-point-title {
  font-size: 0.8rem;
  color: #666;
  margin: 0;
}

.pickup-point-name {
  font-size: 1rem;
  font-weight: 600;
  margin: 0.25rem 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.change-pickup-button {
  padding: 0.5rem;
  background: none;
  border: none;
  color: #026402;
  font-weight: 600;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
}

.change-pickup-icon {
  margin-left: 0.25rem;
}

</style>


{#if isLoading}
  <div class="loader-container">
    <div class="dot-loader">
      <div class="dot"></div>
      <div class="dot"></div>
      <div class="dot"></div>
    </div>
  </div>
{:else}
  <div class="fruit-background {showFruits ? 'show' : ''}">
    {#each Array(200) as _, i}
      {@const fruit = fruits[Math.floor(Math.random() * fruits.length)]}
      <div
        class="fruit"
        style="
          left: {Math.random() * 100}%;
          top: {-100 + (i * 5)}%;
          animation-duration: {30 + Math.random() * 45}s;
          animation-delay: {i * -0.75}s;
          color: {fruit.color};
        "
      >
        {fruit.shape}
      </div>
    {/each}
  </div>

  <div class="animated-top-area" id="animatedTopArea">
    {#each Array(10) as _, i}
      {@const fruit = fruits[Math.floor(Math.random() * fruits.length)]}
      <div
        class="fruit top-area"
        style="
          left: {Math.random() * 100}%;
          top: {-100 + (i * 5)}%;
          animation-duration: {5 + Math.random() * 10}s;
          animation-delay: {i * -0.5}s;
          color: {fruit.color};
        "
      >
        {fruit.shape}
      </div>
    {/each}
  </div>


  <div class="component-content">


    {#if regularCategories.length === 0 && specialCategories.length === 0}
      <div class="no-categories-container">
        <div class="no-categories-icon">📦</div>
        <h2 class="no-categories-title">No Items Available</h2>
        <p class="no-categories-message">We currently have no items available. Please check back later for new offerings.</p>
        <button class="refresh-button" on:click={() => fetchCategories(true)}>
          <span class="refresh-icon">🔄</span> Refresh
        </button>
      </div>
    {:else}

	  <ul>
		{#each regularCategories as category, index}
		  <div
			class="category-container {category.total_quantity <= 2 ? 'out-of-stock' : ''}"
			style="background: {gradients[index % gradients.length]};"
			on:click={() => category.total_quantity > 2 && handleCategoryClick(category.category1)}
			on:keydown={() => category.total_quantity > 2 && handleCategoryClick(category.category1)}
		  >
			<h3>{category.category1__title}</h3>
			{#if category.total_quantity <= 2}
			  <span class="out-of-stock-label">Out Of Stock</span>
			{:else}
			  <div class="live-label">
				Live
				<span class="live-dot"></span>
			  </div>
			{/if}
		  </div>
		{/each}
	  </ul>


		{#if specialCategories.length > 0}
		  <div class="special-trips-header">
			<h2>Group Orders</h2>
			<div class="triangle-icon"></div>
		  </div>

		  <ul>
			{#each specialCategories as category, index}
			  <div
				class="category-container {category.total_quantity <= 2 ? 'out-of-stock' : ''}"
				style="background: {gradients[index % gradients.length]};"
				on:click={() => category.total_quantity > 2 && handleCategoryClick(category.category1)}
				on:keydown={() => category.total_quantity > 2 && handleCategoryClick(category.category1)}
			  >
				<h3>{category.category1__title}</h3>
				{#if category.total_quantity <= 2}
				  <span class="out-of-stock-label">Out Of Stock</span>
				{:else}
				  <div class="live-label">
					Pickup: {formatDate(category.category1__pickup_date)}
				  </div>
				{/if}
			  </div>
			{/each}
		  </ul>
		{/if}

    {/if}

	<!-- Add after the bottom-indicator div but before the closing tag of component-content div -->

	<div class="support-section">

	  <div class="support-card">

		<div class="support-content">

		  <p></p>
		  {#if userProfile?.delivery_point?.whatsapp_group_link}
		  <a href={userProfile.delivery_point.whatsapp_group_link} class="support-button whatsapp-button">
			<span class="support-button-icon">📱</span>
			<span>Join WhatsApp Group</span>
		  </a>
		  {:else}
		  <a href="https://chat.whatsapp.com/DhrBrMDaHoL1c5YTJTi3JI" class="support-button whatsapp-button">
			<span class="support-button-icon">📱</span>
			<span>Join WhatsApp Group</span>
		  </a>
		  {/if}
		</div>
	  </div>
	</div>


  </div>

<div class="bottom-nav">
  <div
    class="nav-item {activeTab === 'home' ? 'active' : ''}"
    on:click={() => {
      closePickupsPopup();
    }}
  >
    <span class="nav-icon">🏠</span>
    <span>Home</span>
  </div>

  <div
    class="nav-item {activeTab === 'orders' ? 'active' : ''}"
    on:click={() => dispatch('pleaseOrderHistory')}
  >
    <span class="nav-icon">📦</span>
    <span>Order History</span>
  </div>

  {#if numberOfDeliveries > 0}
    <div
      class="nav-item pickup-button"
      on:click={togglePickupsPopup}
    >
      <span class="pickup-text">Pickups</span>
      <span class="pickup-badge">{numberOfDeliveries}</span>
    </div>
  {/if}

  <div
    class="nav-item {activeTab === 'settings' ? 'active' : ''}"
    on:click={() => dispatch('profileView')}
  >
    <span class="nav-icon">☰</span>
    <span>Profile</span>
  </div>
</div>

  <div class="popup-overlay {showPickupsPopup ? 'show' : ''}" on:click={closePickupsPopup}></div>


{#if showPickupsPopup}
  <div class="pickups-popup">
    <button class="close-popup" on:click={closePickupsPopup}>×</button>
    <h2>Today Pickup's ({numberOfDeliveries})</h2>
    {#each activePickups as pickup}
      <div class="pickup-card">
        <div class="pickup-code-container">
          <span class="pickup-code-label">Pickup Code</span>
          <span class="pickup-code">{pickup.pickup_code || ''}</span>
        </div>
        <div class="pickup-status">{pickup.pickup_message}</div>
        <div class="pickup-category">
          {pickup.category_title} (Rs.{pickup.order_total_price} {pickup.order_payment_status === 'successful' ? 'Successful' : 'Cash'})
        </div>
        <div class="pickup-point">
          <span class="pickup-point-icon">📍</span>
          <div class="pickup-point-address">
            {pickup.delivery_point_name}<br>
            {pickup.delivery_point_address}
          </div>
        </div>
      </div>
    {/each}
  </div>
{/if}


{/if}

{#if showUpdatePrompt}
  <div class="update-prompt-overlay" on:click={closeUpdatePrompt}>
    <div class="update-prompt" on:click|stopPropagation>
      <h2>New Update Available</h2>
      <p>A new version of the app is available. Update now to get the latest features and improvements!</p>
      <div class="update-prompt-buttons">
        <button class="update-button" on:click={handleUpdate} disabled={isUpdating}>
          {#if isUpdating}
            <div class="update-dot-loader">
              <div class="update-dot"></div>
              <div class="update-dot"></div>
              <div class="update-dot"></div>
            </div>
          {:else}
            Update Now
          {/if}
        </button>
        <button class="close-button" on:click={closeUpdatePrompt}>Close</button>
      </div>
    </div>
  </div>
{/if}